import { ComponentPortal, ComponentType, Portal, TemplatePortal } from '@angular/cdk/portal';
import { Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// Based on https://stackoverflow.com/questions/56772447/change-mat-sidenav-content-dynamically-from-any-component
// or rather https://stackblitz.com/edit/mat-dynamic-panel
// later removed the mat side nav but kept the logic

@Injectable({
    providedIn: 'root',
})
export class OverlayService {
    /**
     *  True if open, else false
     */
    private overlayStateOpenSubject = new BehaviorSubject(false);
    private overlayPortalSubject = new BehaviorSubject<Portal<any> | null>(null);

    private viewContainerRef?: ViewContainerRef;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly overlayPortal = this.overlayPortalSubject.asObservable();

    /**
     *  Emits true if opened, else false
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly overlayStateOpen = this.overlayStateOpenSubject.asObservable();

    /** Sets the view container ref needed for {@link #setOverlayContent}. */
    public setViewContainerRef(vcr: ViewContainerRef) {
        this.viewContainerRef = vcr;
    }

    /** Sets the overlay portal to the specified portal. */
    public setOverlayPortal(panelPortal: Portal<any>) {
        this.overlayPortalSubject.next(panelPortal);
    }

    /**
     * Sets the overlay content.
     *
     * @param content The component/template reference used.
     * @see OverlayService#setOverlayPortal
     */
    public setOverlayContent(content: ComponentType<any> | TemplateRef<any>) {
        if (content instanceof TemplateRef) {
            if (this.viewContainerRef) {
                this.overlayPortalSubject.next(new TemplatePortal(content, this.viewContainerRef));
            }
        } else {
            this.overlayPortalSubject.next(new ComponentPortal(content));
        }
    }

    /** Resets the current overlay portal. */
    public clearOverlayPortal() {
        this.overlayPortalSubject.next(null);
    }

    /** Opens the overlay with optionally a portal to be set. */
    public open(portal?: Portal<any>) {
        if (this.overlayStateOpenSubject.value === true) {
            return;
        }

        if (portal) {
            this.overlayPortalSubject.next(portal);
        }
        this.overlayStateOpenSubject.next(true);
    }

    /** Toggles the overlay. */
    public toggle() {
        this.overlayStateOpenSubject.next(!this.overlayStateOpenSubject.getValue());
    }

    /** Closes the overlay. */
    public close() {
        this.overlayStateOpenSubject.next(false);
    }
}
